import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./Base.module.scss";

import {
  BudgetFormData,
  useBudgetSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import BudgetForm from "@/features/transactions/components/BudgetForm";
import { useChannels } from "@/lib/contexts/Channel/useChannels";
import { usePopup } from "@/lib/hooks/usePopup";
import useToast from "@/lib/hooks/useToast";

interface EditChannelMonthlyBudgetPopupProps {
  budget: number;
  channelId: string;
  clientId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditChannelMonthlyBudgetInner = ({
  budget,
  channelId,
  clientId,
  onCancel,
  onSuccess,
}: EditChannelMonthlyBudgetPopupProps) => {
  const { disableClose, enableClose } = usePopup();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { budgetSchema } = useBudgetSchema();

  const { updateMonthlyBudget } = useChannels();

  const methods = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    mode: "onChange",
    defaultValues: {
      budget: budget || 0,
    },
  });

  const handleSubmit = (data: BudgetFormData) => {
    updateMonthlyBudget.mutate(
      {
        channelId,
        payload: { budget: data.budget, clientId2: clientId },
      },
      {
        onSuccess: () => {
          showToast(
            t("success.updated", { entity: t("common.forecast") }),
            "success"
          );
          onSuccess();
          methods.reset();
          methods.clearErrors();
        },
      }
    );
  };

  useEffect(() => {
    if (updateMonthlyBudget.isPending) {
      disableClose();
    } else {
      enableClose();
    }
  }, [updateMonthlyBudget.isPending, disableClose, enableClose]);

  const errorCode = updateMonthlyBudget.error?.response?.data?.[0]?.errorCode;

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t("actions.edit", { entity: t("common.forecast") })}
        </h3>
      </div>
      <BudgetForm
        isLoading={updateMonthlyBudget.isPending}
        onSubmit={handleSubmit}
        methods={methods}
        label={t("common.forecast")}
        error={errorCode && t(`errorCodes.${errorCode}`)}
      />
      <div className={styles.actions}>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={updateMonthlyBudget.isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button
          loading={updateMonthlyBudget.isPending}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <Icons.AddIcon fontSize={24} />
          {t("actions.save", { entity: t("common.forecast") })}
        </Button>
      </div>
    </>
  );
};

export const EditChannelMonthlyBudgetPopup = (
  props: EditChannelMonthlyBudgetPopupProps
) => {
  return <EditChannelMonthlyBudgetInner {...props} />;
};
